import type { CoverDoc } from 'fym-shared/src/music24/coverDoc';
import type { CreateCoverMakerLeadOptions } from '../../../redux/api/fymAPI.types';
import { coverErrorCode, coverErrorStatus } from '../doc/coverEditorErrors';

/** What a refused or failed lead means for the email dialog (design cover-editor g3). */
export type CoverLeadError =
  /** The address is not an email address (the field, or a 400 of the API). */
  | 'invalid'
  /** 400 `M24_DISPOSABLE_EMAIL`: a temporary address. */
  | 'disposable'
  /** 429 `M24_RATE_LIMITED`: 10 downloads an hour from one network. */
  | 'rate'
  /** Another 4xx: the API refused the request; the download does not start. */
  | 'failed'
  /** No answer or a 5xx: the API is down. The download starts and the lead goes later. */
  | 'down';

// One `@`, no space, and a dot in the host. The API checks the address again.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const EMAIL_MAX = 320;
const FIELD_MAX = 200;

/** The address as the API takes it, or null when it does not look like an email address. Pure. */
export function coverLeadEmail(value: string): string | null {
  const email = value.trim();
  return email.length <= EMAIL_MAX && EMAIL_PATTERN.test(email) ? email : null;
}

const fieldOf = (value: string): string | undefined => {
  const text = value.trim().slice(0, FIELD_MAX);
  return text === '' ? undefined : text;
};

/**
 * The body of `POST /public/cover-maker/leads` (spec §5, owner decision D12). An empty title or
 * artist and a missing thumbnail are left out. `trackingConsent` is the choice of the consent
 * gate: true only after an Accept. `leadToken` is the token that this browser got before: the API
 * updates that row and keeps the token (an email address alone never gives a token). Pure.
 */
export function coverLeadRequest(input: {
  email: string;
  doc: CoverDoc;
  title: string;
  artist: string;
  thumbnail: string | null;
  locale: string;
  trackingConsent: boolean;
  leadToken: string | null;
}): CreateCoverMakerLeadOptions {
  const title = fieldOf(input.title);
  const artist = fieldOf(input.artist);
  return {
    email: input.email.trim(),
    doc: input.doc,
    ...(title === undefined ? {} : { title }),
    ...(artist === undefined ? {} : { artist }),
    ...(input.thumbnail === null ? {} : { thumbnail: input.thumbnail }),
    locale: input.locale.slice(0, 16),
    trackingConsent: input.trackingConsent,
    ...(input.leadToken === null ? {} : { leadToken: input.leadToken }),
  };
}

/** Maps a failed lead request to what the dialog shows. A failure with no HTTP answer is `down`. Pure. */
export function coverLeadError(error: unknown): CoverLeadError {
  const code = coverErrorCode(error);
  const status = coverErrorStatus(error);
  if (code === 'M24_DISPOSABLE_EMAIL') {
    return 'disposable';
  }
  if (code === 'M24_RATE_LIMITED' || status === 429) {
    return 'rate';
  }
  if (status === null || status >= 500) {
    return 'down';
  }
  // A 400 with no code is the Joi check of the address.
  return status === 400 && code === null ? 'invalid' : 'failed';
}

/**
 * True when Download asks for the email first (D12): a visitor with no account who did not leave
 * an email in this browser yet. The email is asked one time for each browser; an email that waits
 * for the API (it was down at the first download) counts as left. Pure.
 */
export function needsLeadEmail(input: {
  isSignedIn: boolean;
  leadToken: string | null;
  pendingEmail: string | null;
}): boolean {
  return !input.isSignedIn && input.leadToken === null && input.pendingEmail === null;
}
